import axios from "axios";

import { getApiBaseUrl, pget, ppost } from "@/api";
import { Models } from "@/api/types";
import { storage } from "@/modules/storage";
import { queryClient } from "@/pages/Root";
import Services from "@/services/Services";

export const acknowledgeGiftCardTransferById = ppost(
  "/gift-cards/transfers/acknowledge/{transferId}",
);

export const getNotAcknowledgedGiftCards = pget("/gift-cards/transfers/not-acknowledged");

export const refreshNotAcknowledgedGiftCardsQuery = () => {
  queryClient.invalidateQueries(getNotAcknowledgedGiftCards.keyed());
};

export const getGiftCardTransfersByOrderId = pget("/gift-cards/transfers/by-order/{orderId}");

export const resendGiftCardNotification = async (args: { giftCardTransferItemId: string }) => {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/api/v1/gift-cards/transfers/${args.giftCardTransferItemId}/resend`;
  await axios.post(url, {});
  return true;
};

export const transformGiftCardData = (data: Models<"DisplayGiftCardListing">) => {
  return {
    packageListingId: data.id,
    backgroundImage: data.imageUri,
    textColor: "#000000",
    name: data.name.text,
    prices: data.giftCards.map((giftCard) => ({
      packageId: giftCard.packageId,
      packagePriceId: giftCard.packagePriceId,
      price: giftCard.baseCostInCents,
    })),
  };
};

const getUser = pget("/user");

export const getGiftCardInfo = async (request: Request) => {
  const authorized = await Services.auth.isAuthorized();

  if (!authorized) {
    storage.removeItem("giftCardInfo");
  }

  const giftCardInfo = storage.getItem("giftCardInfo");
  const giftCardInfoObject = giftCardInfo ? JSON.parse(giftCardInfo) : null;

  const searchParams = new URL(request.url).searchParams;
  const giftCardListingId =
    searchParams.get("giftCardListingId") || giftCardInfoObject?.giftCardListingId;
  const giftCardItemId = searchParams.get("giftCardItemId") || giftCardInfoObject?.giftCardItemId;
  const sharingPersonFirstName =
    searchParams.get("sharingPersonFirstName") || giftCardInfoObject?.sharingPersonFirstName;
  const sharingPersonLastName =
    searchParams.get("sharingPersonLastName") || giftCardInfoObject?.sharingPersonLastName;
  const sharingPersonPhone =
    searchParams.get("sharingPersonPhone") || giftCardInfoObject?.sharingPersonPhone;
  const sharingPersonEmail =
    searchParams.get("sharingPersonEmail") || giftCardInfoObject?.sharingPersonEmail;
  const storedAuthToken = await Services.auth.getToken();
  const intendedBuyerAuthToken = searchParams.get("intendedBuyerAuthToken") || storedAuthToken;

  let intendedBuyerUser = null;

  if (intendedBuyerAuthToken) {
    storage.setItem(
      "giftCardInfo",
      JSON.stringify({
        giftCardListingId,
        giftCardItemId,
        sharingPersonFirstName,
        sharingPersonLastName,
        sharingPersonPhone,
        sharingPersonEmail,
      }),
    );

    await Services.auth.setToken(intendedBuyerAuthToken);

    try {
      intendedBuyerUser = await queryClient.fetchQuery({ ...getUser.keyed(), staleTime: 0 });
    } catch (error) {
      console.error("Failed to fetch buyer user data:", error);
    }
  }

  return {
    giftCardListingId,
    giftCardItemId,
    sharingPersonFirstName,
    sharingPersonLastName,
    sharingPersonPhone,
    sharingPersonEmail,
    intendedBuyerUser,
  };
};

type calculatePriceInCentsForGiftCardItemArgs = {
  basePriceCents: number;
  percentageOff: number;
  amountOffInCents: number;
};
export const calculatePriceInCentsForGiftCardItem = ({
  basePriceCents,
  percentageOff,
  amountOffInCents,
}: calculatePriceInCentsForGiftCardItemArgs) => {
  if (percentageOff > 0) {
    const discountedPrice = basePriceCents - basePriceCents * percentageOff;
    return discountedPrice;
  }
  if (amountOffInCents > 0) {
    const discountedPrice = basePriceCents - amountOffInCents;
    return discountedPrice;
  }
  return basePriceCents;
};

export const convertGiftCardItemsDiscountsToMap = (
  giftCardItemsDiscounts: Models<"DisplayGiftCardItemsDiscounts">[],
): Record<string, Models<"ShopProductDiscountModel"> | undefined> => {
  const discountMap: Record<string, Models<"ShopProductDiscountModel"> | undefined> = {};

  giftCardItemsDiscounts?.forEach((item) => {
    if (item.discounts && item.discounts.length > 0) {
      const bestDiscount = item.discounts.reduce((best, current) => {
        const bestValue = best.percentOff > 0 ? best.percentOff : best.amountOff;
        const currentValue = current.percentOff > 0 ? current.percentOff : current.amountOff;
        return currentValue > bestValue ? current : best;
      }, item.discounts[0]);

      discountMap[item.giftCardItemId] = {
        maxPercentageOff: bestDiscount.percentOff,
        maxAmountOff: bestDiscount.amountOff,
        isFromSiteWidePromo: false,
        isVariable: false,
        title: null,
        isBuyXGetY: false,
        matchesRequirementsAndInclusions: true,
        discount: null,
        isSignUpReward: false,
      };
    }
  });

  return discountMap;
};
